/**
 * `draftToSoap.ts` — Phase 3 item 3.1 (LD-18).
 *
 * The AI scribe returns FHIR-shaped resources (`GenerateDraftResponseDto`)
 * directly via Anthropic tool_use. The frontend reads that into RHF state
 * (`ProgressNoteEditFormValues`). This adapter is the single translation from
 * that form state into the `SoapNoteDto` consumed by
 * `POST /visits/:encounterId/soap-note`. Both the draft auto-save / explicit
 * Save AND the AI auto-submit go through `draftToSoap` → that one endpoint.
 *
 * Gate-clinical-writes-until-Lock — what the endpoint does with the DTO depends
 * on the note's lifecycle state, NOT on this adapter:
 *   - While the note is PRELIMINARY (unlocked): the endpoint persists ONLY the
 *     draft — a QuestionnaireResponse plus a single find-or-update preliminary
 *     `DocumentReference` carrying a lossless `scribe-draft-payload` blob (see
 *     `scribeDraftPayload.ts`). NO Conditions / Procedures / MedicationStatements
 *     / Observations / AllergyIntolerance / ServiceRequest / Claim are written
 *     yet. Save-draft is idempotent (find-or-update), so repeated auto-saves
 *     don't fan out duplicate resources.
 *   - At LOCK (note → `final`): the stored draft blob is replayed server-side
 *     and the full `evaluateSoapNote` cascade writes the clinical resources +
 *     Provenance and flips the DocumentReference to `final`.
 *
 * So there very much IS an intermediate draft layer (the preliminary DocRef +
 * blob) and a save-draft path — they're just upstream of this pure adapter.
 *
 * The LOINC vital-code map mirrors `middleware/src/visit/visit.utils.ts:40-106`
 * (kept in sync by hand — small, low-churn).
 */
import type {
  Allergy,
  BillingEntry as ContractBillingEntry,
  BillingSuggestion as ContractBillingSuggestion,
  Coding,
  Condition,
  ServiceRequest,
  SoapNote,
  Treatment,
} from '@behuman/core-contract'
import { lowConfidenceKey } from '@behuman/core-contract'
import {
  getSelectedCandidate,
  type AllergyItem,
  type BillingEntry,
  type BillingSuggestion,
  type ConditionItem,
  type MedicationItem,
  type ObservationItem,
  type ProgressNoteEditFormValues,
  type RosEntry,
  type ServiceRequestItem,
} from './progressNoteEditSchema'
import { CPT_SYSTEM, ICD10_SYSTEM, LOINC_SYSTEM, RXNORM_SYSTEM, SNOMED_SYSTEM } from './fhirSystems'
import { VITAL_UNIT_FIELDS, canonicalVitalCode, vitalUnitToUcum } from './vitalUnits'
import { fromClinicDateTimeLocal } from '@/utils'

// Zod-inferred contract types from `@behuman/core-contract` (source of truth:
// `middleware/src/visit/soap-note-payload.contract.ts`). The `*Dto` alias names
// are kept so downstream importers (scribeDraftPayload.ts, progressNotePdf.ts,
// LockedProgressNoteSections.tsx, …) are unaffected by the switch off the
// stale generated OpenAPI types.
export type SoapNoteDto = SoapNote
type CodingDto = Coding
type ConditionDto = Condition
type AllergyDto = Allergy
type ServiceRequestDto = ServiceRequest
type TreatmentDto = Treatment
type BillingEntryDto = ContractBillingEntry
type BillingSuggestionDto = ContractBillingSuggestion

/**
 * Mirrors `middleware/src/visit/visit.utils.ts:40-106` (vitals LOINC map).
 * When the AI returns an observation with one of these LOINC codes, populate
 * the matching typed `SoapNoteDto` vital field instead of pushing to the
 * generic observations bucket.
 *
 * `note` is the matching free-text note field in `SoapNoteDto` (`bpNote`,
 * `hrNote`, etc.). `bmi` has no companion note field today.
 */
const LOINC_VITAL_MAP: Record<
  string,
  {
    valueField: keyof SoapNoteDto
    noteField?: keyof SoapNoteDto
    /** When true, this code drives the systolic; the matching diastolic
     * shares `bpNote` (handled separately below). */
    isBpSystolic?: boolean
    isBpDiastolic?: boolean
  }
> = {
  '8480-6': { valueField: 'bpSystolic', noteField: 'bpNote', isBpSystolic: true },
  '8462-4': { valueField: 'bpDiastolic', noteField: 'bpNote', isBpDiastolic: true },
  '8867-4': { valueField: 'hr', noteField: 'hrNote' },
  '9279-1': { valueField: 'rr', noteField: 'rrNote' },
  '8310-5': { valueField: 'temp', noteField: 'tempNote' },
  '29463-7': { valueField: 'wt', noteField: 'wtNote' },
  // Glucose collapses into one `bloodGlucose` slot — the AI may emit
  // 2339-0 (glucose) or 41653-7 (glucometer); both map to the same field.
  '2339-0': { valueField: 'bloodGlucose', noteField: 'bloodGlucoseNote' },
  '41653-7': { valueField: 'bloodGlucose', noteField: 'bloodGlucoseNote' },
  '8302-2': { valueField: 'ht', noteField: 'htNote' },
  '59408-5': { valueField: 'o2', noteField: 'o2Note' },
  '72514-3': { valueField: 'pain', noteField: 'painNote' },
  '39156-5': { valueField: 'bmi' },
  '9843-4': { valueField: 'hc', noteField: 'hcNote' },
}

/** Whether a code writes a typed `SoapNoteDto` vital slot on save (else it flattens to prose). */
export const isTypedVitalCode = (code: string): boolean => LOINC_VITAL_MAP[code] !== undefined

function toCondition(item: ConditionItem): ConditionDto {
  // Carry the two fields the chart's Diagnoses tab renders — `clinicalStatus`
  // (Status column) and `onsetDateTime` (Started column) — so they round-trip
  // to `Condition.clinicalStatus` / `onsetDateTime` on the cascade (previously
  // flattened to a bare coding → chart always read "Active", no onset).
  // `null` form defaults coalesce to `undefined` so they're omitted on the wire.
  return {
    system: ICD10_SYSTEM,
    code: item.code || undefined,
    display: item.display || undefined,
    clinicalStatus: item.clinicalStatus,
    onsetDateTime: item.onsetDateTime ?? undefined,
    // Chart-seeded row — the pastMedicalHistory path skips it at Lock.
    external: item.external,
  }
}

function toProcedureCoding(code: string, display: string): CodingDto {
  return {
    system: CPT_SYSTEM,
    code: code || undefined,
    display: display || undefined,
  }
}

/** PD-1600 — the rows still flagged low-confidence, as `soapNote.lowConfidenceFields`; absent when none. */
function lowConfidenceFieldsOf(form: ProgressNoteEditFormValues): string[] | undefined {
  const keys = [
    ...form.medications
      .filter((m) => m.needsReview)
      .map((m) => lowConfidenceKey('medication', m.code, m.display)),
    ...form.allergies
      .filter((a) => a.needsReview)
      .map((a) => lowConfidenceKey('allergy', a.code, a.display)),
    ...form.observations
      .filter((o) => o.needsReview && o.reviewReason)
      .map((o) => lowConfidenceKey('observation', o.code, o.display)),
  ]
  return keys.length ? keys : undefined
}

function toTreatmentDto(item: MedicationItem): TreatmentDto {
  // Preserve the doctor's free-text dose / route / frequency / note so they
  // round-trip into `MedicationStatement.dosage[]` on the BE cascade (see
  // `middleware/src/visit/visit.utils.ts:toMedicationStatement`). Empty
  // strings collapse to `undefined` so we don't write blank FHIR fields.
  return {
    system: RXNORM_SYSTEM,
    code: item.code || undefined,
    display: item.display || item.code || undefined,
    dose: item.dose?.trim() || undefined,
    route: item.route?.trim() || undefined,
    frequency: item.frequency?.trim() || undefined,
    note: item.note?.trim() || undefined,
  }
}

function toAllergyCoding(item: AllergyItem): AllergyDto {
  // Carry the fields the chart's Allergies tab renders — `clinicalStatus`
  // (Status), `reaction` (Reaction), `category` (Drug vs Non-Drug) — so they
  // round-trip to the AllergyIntolerance on the cascade.
  return {
    system: item.system || SNOMED_SYSTEM,
    code: item.code || undefined,
    display: item.display || undefined,
    clinicalStatus: item.clinicalStatus,
    reaction: item.reaction ?? undefined,
    severity: item.severity ?? undefined,
    category: item.category ?? undefined,
    // Persist the real boolean (incl. `false`): a new blob is self-describing,
    // so the reopen backfill only fires for pre-flag legacy blobs.
    external: item.external,
    // PD-1141 — round-trip the chart-row pointer + the "Updated" marker
    // through the blob (never written back to the chart).
    chartId: item.chartId ?? undefined,
    updated: item.updated,
  }
}

function toServiceRequestCoding(item: ServiceRequestItem): ServiceRequestDto {
  // Carry category + intent + priority + reasonText so they round-trip to
  // `ServiceRequest.category` / `intent` / `priority` / `reasonCode` on the
  // cascade — without category the order was invisible in the chart tabs.
  return {
    system: item.system || LOINC_SYSTEM,
    code: item.code || undefined,
    display: item.display || undefined,
    category: item.category,
    intent: item.intent,
    priority: item.priority ?? undefined,
    reasonText: item.reasonText ?? undefined,
  }
}

/**
 * Translate an AI observation entry to either a typed vital field write or
 * an appendable text fragment for the `objective` narrative.
 *
 * Returns the (field, value, optional note) tuple for vital writes, or
 * `null` when the observation is not a recognized vital code.
 */
function vitalWriteForObservation(item: ObservationItem): {
  valueField: keyof SoapNoteDto
  noteField?: keyof SoapNoteDto
  value: string
  note: string
} | null {
  const mapping = LOINC_VITAL_MAP[item.code]
  if (!mapping) return null

  const value =
    item.valueQuantity != null ? String(item.valueQuantity.value) : item.valueString || ''
  const note = item.note || ''
  return {
    valueField: mapping.valueField,
    noteField: mapping.noteField,
    value,
    note,
  }
}

/**
 * Convert observations that don't map to a typed `SoapNoteDto` vital slot
 * into a single narrative paragraph suitable for appending to `objective`.
 *
 * Per LD-21 the doctor can `+ Add Vital Row` with a free-text label + value
 * (no LOINC code). Those entries have `category === 'vital-signs'` but no
 * matching `LOINC_VITAL_MAP` row — we still want them surfaced on the BE
 * submit, so the filter is simply "not a typed vital write". Exam,
 * laboratory, and social-history observations also fall through here.
 */
function formatNonVitalObservations(observations: ObservationItem[]): string {
  const nonVital = observations.filter((o) => !LOINC_VITAL_MAP[o.code])
  if (nonVital.length === 0) return ''

  return nonVital
    .map((o) => {
      const valueStr =
        o.valueQuantity != null
          ? `${o.valueQuantity.value} ${o.valueQuantity.unit}`
          : o.valueString || ''
      const noteStr = o.note ? ` (${o.note})` : ''
      // Label-less rows (the hydrated exam-narrative row and the Examination
      // section's free-prose rows) re-serialize verbatim — a prefix here would
      // nest one wrapper per save cycle.
      if (!o.code && !o.display) {
        return `${valueStr}${noteStr}`.trim()
      }
      const display = o.display || o.code || 'Observation'
      const valuePart = valueStr ? `: ${valueStr}` : ''
      return `${display}${valuePart}${noteStr}`
    })
    .filter((line) => line.trim().length > 0 && line !== 'Observation')
    .join('\n')
}

/**
 * Convert the AI's billing-suggestion block into `SoapNoteDto.billing[]`
 * (a `BillingEntryDto[]`). One entry per code:
 *   - 1× primary E/M code (the SELECTED candidate per `selectedRank`) with
 *     all modifiers attached
 *   - 1× per add-on code (G2211 etc.) with the same modifiers attached
 * POS is NOT written to `BillingEntryDto` (no slot, and the cascade caps
 * modifiers at 3 — a 4th would be dropped). It round-trips for display via the
 * `ai-billing-suggestion` DocumentReference extension instead.
 */
function billingSuggestionToBillingEntries(
  billing: BillingSuggestion,
  existingBillingEntries: BillingEntry[],
  encounterDiagnoses: ConditionItem[],
  priceFor?: (code: string) => number | undefined,
  refusedFor?: (code: string) => string | undefined,
): BillingEntryDto[] {
  // If the doctor already populated the existing Claim/BillingModal manually,
  // honor that and skip the AI suggestion translation.
  if (existingBillingEntries.length > 0) {
    return existingBillingEntries.map((entry) => ({
      procedure: entry.procedure || {},
      // A $0 entry is never a deliberate charge (explicit saves refuse it), so
      // it takes the schedule price once one is known.
      price: entry.price || (entry.procedure?.code ? priceFor?.(entry.procedure.code) : 0) || 0,
      quantity: entry.quantity,
      billingItems: entry.billingItems.map((item) => ({ dx: item.dx || {} })),
      modifiers: entry.modifiers,
    }))
  }

  const selected = getSelectedCandidate(billing)
  if (!selected?.code) return []

  // Link the visit's encounter diagnoses (primary first, by `rank`) onto every
  // billed line so `toClaim` fills `Claim.diagnosis[]` + the procedure's
  // `diagnosisSequence`. Without this the AI diagnoses persist only as standalone
  // Conditions and the Claim bills an E/M with an empty `diagnosis[]`.
  const dx = [...encounterDiagnoses]
    .filter((c) => c.code)
    .sort((a, b) => (a.rank ?? Number.MAX_SAFE_INTEGER) - (b.rank ?? Number.MAX_SAFE_INTEGER))
    .map((c) => ({ system: ICD10_SYSTEM, code: c.code, display: c.display || undefined }))

  const modifiers = billing.modifiers.slice(0, 3)
  const buildEntry = (code: string, display: string): BillingEntryDto => ({
    procedure: { system: CPT_SYSTEM, code, display },
    price: priceFor?.(code) ?? 0,
    quantity: 1,
    billingItems: dx.map((coding) => ({ dx: coding })),
    modifiers,
  })

  // PD-1647 — the AI's top candidate is selected by default, so without this an
  // RN's note bills a physician E/M code the doctor never chose. The candidate
  // chips still render, so the refusal stays visible and a billable code can be
  // picked instead.
  const entries: BillingEntryDto[] = []
  if (refusedFor?.(selected.code)) return entries
  entries.push(buildEntry(selected.code, selected.display))
  for (const addOn of billing.addOnCodes) {
    if (refusedFor?.(addOn.code)) continue
    entries.push(buildEntry(addOn.code, addOn.display))
  }
  return entries
}

/**
 * Compose any non-empty narrative fragments into a single string field.
 * Used for `objective` (vitals notes + non-vital observation summary) and
 * `plan` (plan narrative + follow-up list).
 */
function joinNonEmpty(...parts: string[]): string {
  return parts.filter((p) => p && p.trim().length > 0).join('\n\n')
}

/**
 * Flatten ROS rows into the `objective`-prefix narrative. Exported so the
 * hydrate side (`hydrateExamNarrativeFromScribeDraft`) can rebuild and strip
 * the exact same prefix — keep both directions in lockstep.
 */
export function formatRosNarrative(ros: RosEntry[]): string {
  return ros
    .map((row) => {
      const system = (row.system ?? '').trim()
      const finding = (row.finding ?? '').trim()
      if (!system && !finding) return ''
      if (!system) return finding
      if (!finding) return system
      return `${system}: ${finding}`
    })
    .filter((line) => line.length > 0)
    .join('\n')
}

/**
 * Translate the FE `BillingSuggestion` (form state) into the contract
 * `BillingSuggestionDto` carried on `SoapNoteDto.billingSuggestion`. The
 * middleware persists this as an Extension on the Progress Note
 * `DocumentReference` so the locked view can rehydrate the full chip set +
 * rationale (reasoning / duration / POS).
 *
 * Returns `undefined` when the suggestion is fully empty — the DocumentReference
 * stays extension-free for non-scribe-authored notes that never went through
 * the AI pipeline.
 */
function billingSuggestionToDto(
  suggestion: BillingSuggestion | undefined,
): BillingSuggestionDto | undefined {
  if (!suggestion) return undefined

  // Drop fully-blank rows so a stray "Add time entry" click can't reach the
  // note. A row with minutes but no label is KEPT: dropping it would silently
  // shrink the total the provider reviewed, and they can see and fix the blank
  // label before signing.
  const timeEntries = (entries: BillingSuggestion['providerTimeEntries']) =>
    entries
      .filter((e) => e.label.trim() !== '' || e.minutes > 0)
      .map((e) => ({
        label: e.label.trim(),
        minutes: e.minutes,
      }))
  const providerEntries = timeEntries(suggestion.providerTimeEntries)
  const careCoordinatorEntries = timeEntries(suggestion.careCoordinatorTimeEntries)
  const hasTotalTime = providerEntries.length > 0 || careCoordinatorEntries.length > 0

  const hasContent =
    suggestion.candidates.length > 0 ||
    !!suggestion.reasoning ||
    suggestion.durationMinutes > 0 ||
    !!suggestion.placeOfService ||
    suggestion.addOnCodes.length > 0 ||
    suggestion.modifiers.length > 0 ||
    hasTotalTime ||
    (suggestion.diagnoses?.length ?? 0) > 0

  if (!hasContent) return undefined

  return {
    candidates: suggestion.candidates.map((c) => ({
      code: c.code,
      display: c.display,
      rank: c.rank,
      reasoning: c.reasoning,
    })),
    selectedRank: suggestion.selectedRank,
    addOnCodes: suggestion.addOnCodes.map((a) => ({
      code: a.code,
      display: a.display,
      reasoning: a.reasoning ?? '',
    })),
    modifiers: suggestion.modifiers,
    placeOfService: suggestion.placeOfService,
    durationMinutes: suggestion.durationMinutes,
    reasoning: suggestion.reasoning,
    ...(hasTotalTime ? { totalTime: { providerEntries, careCoordinatorEntries } } : {}),
    ...(suggestion.diagnoses ? { diagnoses: suggestion.diagnoses } : {}),
  }
}

/**
 * Phase 3 (LD-18) — translate RHF form state to the legacy `SoapNoteDto`.
 *
 * @param form The current RHF values.
 * @param existing An optional existing partial DTO (e.g. dehydrated from a
 *                 prior provenance) — its scalar fields are used as
 *                 fall-backs so we don't drop user edits made before the AI
 *                 draft populated the form.
 */
export function draftToSoap(
  form: ProgressNoteEditFormValues,
  existing?: Partial<SoapNoteDto>,
  /** Org catalog price per CPT — the AI-suggested rows would otherwise save at $0 (PD-1597). */
  priceFor?: (code: string) => number | undefined,
  /** Codes the rendering provider's credential may never bill (PD-1647). */
  refusedFor?: (code: string) => string | undefined,
): SoapNoteDto {
  const conditionsByCategory = {
    'encounter-diagnosis': [] as ConditionItem[],
    'problem-list-item': [] as ConditionItem[],
  }
  for (const c of form.conditions) {
    conditionsByCategory[c.category].push(c)
  }

  // --- Medications: split by intent ---
  const medicationsByIntent = {
    'new-prescription': [] as MedicationItem[],
    continuation: [] as MedicationItem[],
    discontinuation: [] as MedicationItem[],
  }
  for (const m of form.medications) {
    medicationsByIntent[m.intent].push(m)
  }

  // --- Observations: vitals to typed fields, rest to objective narrative ---
  const vitalWrites: Partial<Record<keyof SoapNoteDto, string>> = {}
  for (const obs of form.observations) {
    const write = vitalWriteForObservation(obs)
    if (!write) continue
    // Alias rows can share a slot (glucose 2339-0/41653-7) — never let an
    // empty row wipe a populated one.
    if (!write.value && vitalWrites[write.valueField]) continue
    vitalWrites[write.valueField] = write.value
    if (write.noteField && write.note) {
      vitalWrites[write.noteField] = write.note
    }
    // Convertible vitals also persist the chosen unit as its UCUM token.
    const unitField = VITAL_UNIT_FIELDS[write.valueField] as keyof SoapNoteDto | undefined
    if (unitField && obs.valueQuantity != null) {
      vitalWrites[unitField] = vitalUnitToUcum(canonicalVitalCode(obs.code), obs.valueQuantity.unit)
    }
  }

  // Flatten `summary.ros` into the legacy `objective` string for the BE CCDA builder
  // + evaluator; also written structured below. Empty rows skipped on both.
  const rosNarrative = formatRosNarrative(form.summary.ros)

  // Structured ROS slot for the locked viewer (no `objective` re-parse). Drop empty rows.
  const rosRows = form.summary.ros.filter(
    (row) => row.system.trim().length > 0 || row.finding.trim().length > 0,
  )
  // PD-1804: no rows in the form → keep the stored rows the views hide (`existing`),
  // without folding them into `objective`. Rows the doctor enters replace them.
  const reviewOfSystemsStructured =
    rosRows.length > 0 ? rosRows : (existing?.reviewOfSystemsStructured ?? [])

  const objectiveNarrative = joinNonEmpty(
    rosNarrative, // ROS lives in `objective` per the legacy SOAP-DTO mapping
    formatNonVitalObservations(form.observations),
  )

  // Filter whitespace-only follow-ups — the section's `+ Add Follow-Up Item`
  // button appends an empty row with an inline `<Textarea>`, so rows the
  // doctor never typed into are dropped here before they hit the BE.
  // Inner newlines flatten to spaces: the `followUp` slot, the plan fold, and
  // the hydrate-side suffix-strip all split on `\n` and must agree per entry.
  const followUpEntries = form.followUps
    .map((f) => f.text.replace(/\n+/g, ' ').trim())
    .filter((t) => t.length > 0)
  const followUpText = followUpEntries.map((t) => `• ${t}`).join('\n')
  const planNarrative = joinNonEmpty(form.summary.plan, followUpText)

  // `noteDate` is the doctor-editable "Added on" (PD-1083) and wins; the
  // The editable noteDate wins; the first Encounter-Details row is a legacy
  // fallback only. Extra rows/description have no SoapNoteDto slot (UI-only).
  const primaryEncounterRow = form.encounterDetails[0]
  // datetime-local is naked clinic-tz wall-clock — send a UTC instant, else the BE re-reads it as server-local.
  const resolvedNoteDate = fromClinicDateTimeLocal(
    form.noteDate || primaryEncounterRow?.date?.trim() || existing?.noteDate,
  )

  const soap: SoapNoteDto = {
    // --- Scalar header ---
    noteDate: resolvedNoteDate,
    // `form.visitType` is the single source of truth (PD-1288 dropdown); the
    // Encounter-Details row's `type` string is a display-only seed.
    visitType: form.visitType,

    // --- Narrative SOAP buckets ---
    examReason: form.summary.chiefComplaint,
    // Per LD-21 — `summary.hpi` is now a paragraph array on the FE; the legacy
    // `SoapNoteDto.prob` slot is still a single string, so join with blank-line
    // separators. Empty paragraphs are dropped to avoid double blank lines.
    prob: form.summary.hpi
      .map((p) => (p.text ?? '').trim())
      .filter((p) => p.length > 0)
      .join('\n\n'),
    objective: objectiveNarrative,
    assessment: form.summary.assessment,
    plan: planNarrative,

    // --- Vitals (Phase 3: AI fills via observations[category=vital-signs]) ---
    bpSystolic: vitalWrites.bpSystolic ?? existing?.bpSystolic ?? '',
    bpDiastolic: vitalWrites.bpDiastolic ?? existing?.bpDiastolic ?? '',
    bpNote: vitalWrites.bpNote ?? existing?.bpNote ?? '',
    hr: vitalWrites.hr ?? existing?.hr ?? '',
    hrNote: vitalWrites.hrNote ?? existing?.hrNote ?? '',
    wt: vitalWrites.wt ?? existing?.wt ?? '',
    wtNote: vitalWrites.wtNote ?? existing?.wtNote ?? '',
    pain: vitalWrites.pain ?? existing?.pain ?? '',
    painNote: vitalWrites.painNote ?? existing?.painNote ?? '',
    ht: vitalWrites.ht ?? existing?.ht ?? '',
    htNote: vitalWrites.htNote ?? existing?.htNote ?? '',
    hc: vitalWrites.hc ?? existing?.hc ?? '',
    hcNote: vitalWrites.hcNote ?? existing?.hcNote ?? '',
    rr: vitalWrites.rr ?? existing?.rr ?? '',
    rrNote: vitalWrites.rrNote ?? existing?.rrNote ?? '',
    o2: vitalWrites.o2 ?? existing?.o2 ?? '',
    o2Note: vitalWrites.o2Note ?? existing?.o2Note ?? '',
    temp: vitalWrites.temp ?? existing?.temp ?? '',
    tempNote: vitalWrites.tempNote ?? existing?.tempNote ?? '',
    bmi: vitalWrites.bmi ?? existing?.bmi ?? '',
    // Optional on the DTO — omit when unstated.
    bloodGlucose: vitalWrites.bloodGlucose ?? existing?.bloodGlucose,
    bloodGlucoseNote: vitalWrites.bloodGlucoseNote ?? existing?.bloodGlucoseNote,

    // --- Doctor-chosen vital units (UCUM tokens; absent → BE defaults) ---
    tempUnit: (vitalWrites.tempUnit ?? existing?.tempUnit) as SoapNoteDto['tempUnit'],
    wtUnit: (vitalWrites.wtUnit ?? existing?.wtUnit) as SoapNoteDto['wtUnit'],
    htUnit: (vitalWrites.htUnit ?? existing?.htUnit) as SoapNoteDto['htUnit'],
    glucoseUnit: (vitalWrites.glucoseUnit ?? existing?.glucoseUnit) as SoapNoteDto['glucoseUnit'],

    // --- Coding arrays (LD-1 mapping) ---
    probHPI: existing?.probHPI ?? [],
    // Rows with neither code nor display are unfinished `+ Add Allergy` rows —
    // auto-saving them would materialize junk AllergyIntolerance at lock.
    allergies: form.allergies
      .filter((a) => (a.code ?? '').trim() || (a.display ?? '').trim())
      .map(toAllergyCoding),
    // Rows with neither code nor display are unfinished `+ Add Condition` /
    // `+ Add Diagnosis` rows — auto-saving them would materialize a code-less
    // Condition at lock (same rule as `allergies` below).
    pastMedicalHistory: conditionsByCategory['problem-list-item']
      .filter((c) => c.code.trim() || c.display.trim())
      .map(toCondition),
    pastSurgicalHistory: existing?.pastSurgicalHistory ?? [],
    familyHistory: existing?.familyHistory ?? [],
    // PD-1377 chart-derived rows — structured slot, blob-only like ROS.
    familyHistoryStructured: form.familyHistory.map(({ relative, conditions, deceased }) => ({
      relative,
      conditions,
      deceased,
    })),
    socialHistory: existing?.socialHistory ?? [],
    habits: existing?.habits ?? [],
    // Continuations + discontinuations write a longitudinal MedicationStatement
    // on the cascade. `medicationStatus` is the doctor's explicit status control
    // (the full FHIR set), NOT the row's intent — except a discontinuation row
    // left at the default `active`, which must still record the halt. Any med
    // marked `stopped` is force-routed here so it always reaches the cascade.
    medsReviewed: [
      ...medicationsByIntent.continuation.map((m) => ({
        ...toTreatmentDto(m),
        medicationStatus: m.status,
      })),
      ...medicationsByIntent.discontinuation.map((m) => ({
        ...toTreatmentDto(m),
        medicationStatus: m.status === 'active' ? ('stopped' as const) : m.status,
      })),
      // New prescriptions the doctor stopped during the visit — surface the
      // discontinuation so the chart records the halt.
      ...medicationsByIntent['new-prescription']
        .filter((m) => m.status === 'stopped')
        .map((m) => ({
          ...toTreatmentDto(m),
          medicationStatus: 'stopped' as const,
        })),
    ],
    reviewOfSystems: existing?.reviewOfSystems ?? [],
    // Computed above (dual-write: structured slot + legacy `objective` flatten).
    reviewOfSystemsStructured,
    physicalExam: existing?.physicalExam ?? [],
    dataReviewed: existing?.dataReviewed ?? [],
    assessment1: conditionsByCategory['encounter-diagnosis']
      .filter((c) => c.code.trim() || c.display.trim())
      .map(toCondition),
    testsOrdered: form.serviceRequests.map(toServiceRequestCoding),
    // Stopped new-prescriptions are emitted as discontinuations in
    // `medsReviewed` above, so exclude them here to avoid a duplicate
    // (active) MedicationStatement on the cascade.
    treatmentsPrescribed: medicationsByIntent['new-prescription']
      .filter((m) => m.status !== 'stopped')
      .map((m) => ({ ...toTreatmentDto(m), medicationStatus: m.status })),
    proceduresAdministered: form.procedures.map((p) => toProcedureCoding(p.code, p.display)),
    carePlanGoals: existing?.carePlanGoals ?? [],
    followUp: followUpEntries.join('\n'),
    mdm: form.summary.mdm.trim() || undefined,
    tags: existing?.tags ?? [],
    // PD-1149 — LOINC codes of vitals still flagged implausible (unresolved by
    // the doctor); round-trips the badge + Lock gate across save/reopen.
    // A low-confidence vital (`reviewReason`) is not an implausible one — it rides
    // `lowConfidenceFields` below, so an English note's blob stays exactly as it was.
    implausibleVitals: form.observations
      .filter((o) => o.needsReview && o.code && !o.reviewReason)
      .map((o) => o.code),
    aiAssisted: form.aiAssisted || undefined,
    // PD-1600 — sticky facts about the AI draft; the server keeps them too.
    encounterLanguage: form.encounterLanguage ?? existing?.encounterLanguage,
    lowConfidenceFields: lowConfidenceFieldsOf(form),

    // --- Billing — AI suggestion → BillingEntryDto[], doctor's BillingModal
    //     overrides take precedence (see helper above). ---
    selectedCoverageId: existing?.selectedCoverageId,
    billing: billingSuggestionToBillingEntries(
      form.billingSuggestion,
      form.billingEntries,
      conditionsByCategory['encounter-diagnosis'],
      priceFor,
      refusedFor,
    ),
    // LD-21 — also pass the AI suggestion through verbatim so the middleware
    // can persist it as a DocumentReference.extension. The locked viewer
    // reads it back to surface reasoning + duration + POS (which the Claim
    // alone does not preserve).
    billingSuggestion: billingSuggestionToDto(form.billingSuggestion),
  }

  return soap
}
